import React from 'react';
import { EventDefinition, EventCategory, Workflow, Tag } from '../../types';
import { CreateEventDefinitionRequest } from '../../services/eventDefinitionService';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Card from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';
import SearchBar from '../../components/ui/SearchBar';
import Select from '../../components/ui/Select';
import EventDefinitionFormModal from './EventDefinitionFormModal';
import TagBadge from '../../components/common/TagBadge';
import {
  EVENT_CATEGORY_OPTIONS,
  getEventCategoryLabel,
} from './triggerOptions';

interface EventsTabProps {
  eventDefinitions: EventDefinition[];
  workflows: Workflow[];
  tags: Tag[];
  onTagsChanged: () => void;
  onSubmit: (data: CreateEventDefinitionRequest, id?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const categoryVariants: Record<EventCategory, 'primary' | 'secondary' | 'success' | 'warning'> = {
  schedule: 'primary',
  external: 'warning',
  internal: 'success',
  workflow: 'secondary',
};

const EventsTab: React.FC<EventsTabProps> = ({
  eventDefinitions,
  workflows,
  tags,
  onTagsChanged,
  onSubmit,
  onDelete,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState<EventCategory | ''>('');
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<EventDefinition | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  const filtered = eventDefinitions.filter(ed => {
    if (categoryFilter && ed.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesName = ed.name.toLowerCase().includes(q);
      const matchesDesc = (ed.description ?? '').toLowerCase().includes(q);
      const matchesTag = ed.tags.some(t => t.name.toLowerCase().includes(q));
      if (!matchesName && !matchesDesc && !matchesTag) return false;
    }
    return true;
  });

  const hasActiveFilters = !!searchQuery || !!categoryFilter;
  const clearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
  };

  const openCreate = () => {
    setEditing(null);
    setIsFormOpen(true);
  };

  const openEdit = (ed: EventDefinition) => {
    setEditing(ed);
    setIsFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    try {
      await onDelete(deletingId);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      {/* Filter / action bar */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search events..."
        />
        <Select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value as EventCategory | '')}
          options={EVENT_CATEGORY_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          placeholder="All categories"
          className="w-48"
        />
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={clearFilters}>
            Clear all
          </Button>
        )}
        <div className="ml-auto">
          <Button variant="primary" size="sm" onClick={openCreate}>
            New Event
          </Button>
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <EmptyState
          title={hasActiveFilters ? 'No events match your filters' : 'No events yet'}
          description={
            hasActiveFilters
              ? 'Try adjusting your search or filters.'
              : 'Define an event to listen for, then build triggers on top of it.'
          }
          action={
            hasActiveFilters
              ? { label: 'Clear filters', onClick: clearFilters }
              : { label: 'New Event', onClick: openCreate }
          }
        />
      ) : (
        <Card variant="default" className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  Event
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  Triggers
                </th>
                <th className="px-4 py-3 w-32 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map(ed => (
                <tr key={ed.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-neutral-900">{ed.name}</div>
                    {ed.description && (
                      <div className="text-xs text-neutral-500 mt-0.5 truncate max-w-md">
                        {ed.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={categoryVariants[ed.category]} size="sm">
                      {getEventCategoryLabel(ed.category)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {ed.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {ed.tags.map(tag => (
                          <TagBadge key={tag.id} tag={tag} />
                        ))}
                      </div>
                    ) : (
                      <span className="text-neutral-400 text-sm">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-600">
                    {ed.triggerCount}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(ed)}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded shadow-sm hover:bg-primary-700 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeletingId(ed.id)}
                        disabled={ed.triggerCount > 0}
                        title={
                          ed.triggerCount > 0
                            ? 'In use — remove dependent triggers first'
                            : 'Delete'
                        }
                        className="px-3 py-1.5 text-xs font-medium text-danger-600 border border-danger-300 rounded shadow-sm hover:bg-danger-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <EventDefinitionFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={async (data, id) => {
          await onSubmit(data, id);
        }}
        editing={editing}
        workflows={workflows}
        tags={tags}
        onTagsChanged={onTagsChanged}
      />

      {deletingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDeletingId(null)} />
          <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-md w-full mx-auto z-10 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              Delete Event Definition
            </h3>
            <p className="text-sm text-neutral-600 mb-4">
              This will permanently delete the event definition. Triggers that
              reference it must be removed first.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeletingId(null)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventsTab;
